import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Pause, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/Badges";
import { api, formatApiError } from "@/lib/api";
import { ACTIVE_STATUSES, PHASES, SOURCE_LABELS, formatDate } from "@/lib/constants";

const COUNTERS = [
  { key: "found", label: "Trouvés" },
  { key: "extracted", label: "Extraits" },
  { key: "merged", label: "Fusionnés" },
  { key: "known", label: "Déjà connus" },
  { key: "enriched", label: "Enrichis" },
];

export const useJobControl = () => {
  const queryClient = useQueryClient();
  return async (id, action) => {
    try {
      await api.post(`/jobs/${id}/${action}`);
      const labels = { pause: "Recherche mise en pause", resume: "Recherche reprise", cancel: "Recherche annulée", retry: "Recherche relancée" };
      toast.success(labels[action]);
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["job", id] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
};

export const JobControls = ({ job, prefix = "job" }) => {
  const control = useJobControl();
  return (
    <div className="flex flex-wrap gap-2">
      {["pending", "running"].includes(job.status) && (
        <Button size="sm" variant="outline" data-testid={`${prefix}-pause-button`} onClick={() => control(job.id, "pause")}>
          <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause
        </Button>
      )}
      {job.status === "paused" && (
        <Button size="sm" variant="outline" data-testid={`${prefix}-resume-button`} onClick={() => control(job.id, "resume")}>
          <Play className="mr-1.5 h-3.5 w-3.5" /> Reprendre
        </Button>
      )}
      {ACTIVE_STATUSES.includes(job.status) && (
        <Button size="sm" variant="outline" data-testid={`${prefix}-cancel-button`} className="text-[#FF3B30] hover:text-[#FF3B30]" onClick={() => control(job.id, "cancel")}>
          <Square className="mr-1.5 h-3.5 w-3.5" /> Annuler
        </Button>
      )}
    </div>
  );
};

const ConnectorList = ({ connectors }) => (
  <div className="divide-y divide-border rounded-md border border-border">
    {Object.entries(connectors || {}).map(([key, c]) => (
      <div key={key} data-testid={`connector-status-${key}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
        <div className="min-w-0">
          <div className="font-medium">{SOURCE_LABELS[key] || key}</div>
          {c.error && <div className="truncate text-xs text-[#FF3B30]" title={c.error}>{c.error}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="font-mono text-xs text-muted-foreground">{c.count ?? 0}</span>
          <StatusBadge status={c.status} />
        </div>
      </div>
    ))}
  </div>
);

const LogFeed = ({ logs }) => (
  <div data-testid="job-logs" className="h-56 overflow-y-auto rounded-md bg-[#0A0A0A] p-4 font-mono text-[11px] leading-relaxed text-zinc-300">
    {[...(logs || [])].reverse().map((l, i) => (
      <div key={i} className={l.level === "error" ? "text-red-400" : l.level === "warn" ? "text-amber-300" : l.level === "success" ? "text-emerald-400" : ""}>
        <span className="text-zinc-500">{new Date(l.ts).toLocaleTimeString("fr-CH")}</span> {l.message}
      </div>
    ))}
  </div>
);

export const JobProgress = ({ jobId }) => {
  const { data: job } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api.get(`/jobs/${jobId}`).then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (q) => (ACTIVE_STATUSES.includes(q.state.data?.status) ? 1500 : false),
  });

  if (!jobId || !job) {
    return (
      <div data-testid="job-progress-empty" className="grid-paper rounded-lg border border-dashed border-border p-10 text-sm text-muted-foreground">
        Aucune recherche active. Lancez une recherche globale pour suivre l'extraction en temps réel.
      </div>
    );
  }

  return (
    <section data-testid="job-progress-panel" className="fade-up rounded-lg border border-border bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-border p-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="eyebrow mb-1">Suivi en temps réel · {formatDate(job.created_at)}</div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-2xl font-bold tracking-tight">{job.keywords} <span className="text-muted-foreground">@ {job.location}</span></h2>
            <StatusBadge status={job.status} testId="job-progress-status" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <JobControls job={job} />
          <Button asChild size="sm" variant="ghost" data-testid="job-view-leads-link">
            <Link to={`/leads?job_id=${job.id}`}>Voir les leads <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      </div>
      <div className="p-6">
        <div className="mb-2 flex justify-between text-sm">
          <span data-testid="job-phase" className="font-medium">{PHASES[job.phase] || job.phase}</span>
          <span data-testid="job-progress-percent" className="font-mono">{Math.round(job.progress || 0)}%</span>
        </div>
        <Progress data-testid="job-progress-bar" value={job.progress || 0} className="h-2" />
        {job.error && <div data-testid="job-error" className="mt-3 text-sm text-[#FF3B30]">{job.error}</div>}
        <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border md:grid-cols-5">
          {COUNTERS.map((c) => (
            <div key={c.key} className="bg-white p-4">
              <div className="eyebrow">{c.label}</div>
              <div data-testid={`job-counter-${c.key}`} className="mt-2 font-display text-3xl font-black">{job.stats?.[c.key] ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ConnectorList connectors={job.connectors} />
          <LogFeed logs={job.logs} />
        </div>
      </div>
    </section>
  );
};
